import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FolderCheck, FolderX, CheckCircle2, Search, Folder } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

export const CompleteDialog = ({ task, open, onOpenChange, onDone }) => {
  const [check, setCheck] = useState(null);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (!open || !task) return;
    setCheck(null);
    setSelected(null);
    setQuery("");
    setResults([]);
    api
      .get(`/tasks/${task.id}/complete-check`)
      .then(({ data }) => setCheck(data))
      .catch(() => setCheck({ mode: "no_folders" }));
  }, [open, task]);

  const search = async () => {
    if (query.trim().length < 2) return;
    setSearching(true);
    try {
      const { data } = await api.get("/drive/folders/search", { params: { q: query.trim() } });
      setResults(data);
      if (!data.length) toast.info("Bu adla klasör bulunamadı.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Klasör aranamadı.");
    } finally {
      setSearching(false);
    }
  };

  const finish = async (folderId, fallback) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/tasks/${task.id}/complete`, {
        move: Boolean(folderId),
        confirm: Boolean(folderId),
        folder_id: folderId,
        fallback: Boolean(fallback),
      });
      toast.success(data.moved ? "İş tamamlandı ve evrak Drive klasörüne taşındı." : "İş tamamlandı.");
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "İşlem tamamlanamadı.");
    } finally {
      setBusy(false);
    }
  };

  const mode = check?.mode;
  const options = [...(check?.candidates || []), ...results.filter(
    (r) => !(check?.candidates || []).some((c) => c.id === r.id)
  )];

  const FolderRow = ({ f }) => (
    <button
      type="button"
      onClick={() => setSelected(f.id)}
      data-testid={`folder-option-${f.id}`}
      className={cn(
        "flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left text-sm transition-colors duration-150",
        selected === f.id
          ? "border-navy bg-navy text-white dark:border-slate-100 dark:bg-slate-100 dark:text-navy"
          : "border-border bg-card hover:bg-muted"
      )}
    >
      <Folder className="h-4 w-4 shrink-0" />
      <span className="truncate">{f.name}</span>
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-lg" data-testid="complete-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-bold tracking-tight">İşi Tamamla</DialogTitle>
          <DialogDescription className="truncate">{task?.title}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          {!check && <p className="text-muted-foreground">Hedef klasör kontrol ediliyor…</p>}

          {mode === "no_file" && (
            <p className="flex items-start gap-2">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
              Bu kayda bağlı Drive evrakı yok. Yalnızca durum “Tamamlandı” olarak güncellenecek.
            </p>
          )}

          {mode === "target" && (
            <p className="flex items-start gap-2">
              <FolderCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
              Evrak <strong>{check.folder_name}</strong> klasörüne taşınacak. Onaylıyor musunuz?
            </p>
          )}

          {mode === "fallback" && (
            <p className="flex items-start gap-2">
              <FolderX className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              Hedef klasör belirlenemedi. Evrak <strong>{check.folder_name}</strong> klasörüne taşınacak
              ve kayda “Dosya klasörü bulunamadı” etiketi eklenecek.
            </p>
          )}

          {mode === "choose" && (
            <div className="space-y-4" data-testid="folder-picker">
              <p className="flex items-start gap-2">
                <FolderX className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                Birden fazla olası hedef klasör bulundu. Otomatik seçim yapılmadı; klasörü siz seçin.
              </p>
              <div className="space-y-2">{options.map((f) => <FolderRow key={f.id} f={f} />)}</div>
              <div className="space-y-2">
                <Label>Klasör Ara</Label>
                <div className="flex gap-2">
                  <Input
                    data-testid="folder-search-input"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && search()}
                    placeholder="Klasör adı"
                  />
                  <Button variant="outline" onClick={search} disabled={searching} data-testid="folder-search-btn">
                    <Search className="h-4 w-4" /> Ara
                  </Button>
                </div>
              </div>
            </div>
          )}

          {mode === "no_folders" && (
            <p className="text-crimson">
              Drive klasör yapısı hazır değil. Ayarlar bölümünden klasörleri oluşturun veya taşımadan
              tamamlayın.
            </p>
          )}
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-row">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="complete-cancel">
            Vazgeç
          </Button>
          {(mode === "target" || mode === "fallback" || mode === "choose") && (
            <Button variant="outline" disabled={busy} onClick={() => finish(null, false)} data-testid="complete-without-move">
              Taşımadan tamamla
            </Button>
          )}
          {mode === "choose" && (
            <Button
              variant="outline"
              disabled={busy || !check?.fallback_folder_id}
              onClick={() => finish(check.fallback_folder_id, true)}
              data-testid="complete-use-fallback"
            >
              Tamamlanan Evraklar'a taşı
            </Button>
          )}
          <Button
            disabled={busy || !check || (mode === "choose" && !selected)}
            onClick={() =>
              finish(
                mode === "choose" ? selected : mode === "target" || mode === "fallback" ? check.folder_id : null,
                mode === "fallback"
              )
            }
            data-testid="complete-confirm"
            className="bg-emerald-600 text-white hover:bg-emerald-700"
          >
            {mode === "target" || mode === "fallback" || mode === "choose"
              ? "Evrakı bu klasöre taşı"
              : "Tamamlandı yap"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
